export interface MenuItem {
  id: string;
  label: string;
  route: string;
  icon?: string;
  description?: string;
  enabled: boolean;
  order: number;
  submenus?: MenuItem[];
}

export interface MenuConfig {
  primaryMenus: MenuItem[];
  footerMenus?: MenuItem[];
  mobileMenus?: MenuItem[];
}

export const MENU_CONFIG: MenuConfig = {
  primaryMenus: [
    {
      id: 'home',
      label: 'Inicio',
      route: '/',
      icon: '🏠',
      description: 'Página principal de Corvus TID',
      enabled: true,
      order: 1
    },
    {
      id: 'abautMe',
      label: 'Acerca de',
      route: '/abautMe',
      icon: '🏢',
      description: 'Información sobre Corvus TID',
      enabled: true,
      order: 2
    },
    {
      id: 'skills',
      label: 'Servicios',
      route: '/skills',
      icon: '⚙️',
      description: 'Nuestros servicios de automatización',
      enabled: true,
      order: 3
    },
    {
      id: 'experience',
      label: 'Experiencia',
      route: '/experience',
      icon: '💼',
      description: 'Nuestra experiencia profesional',
      enabled: false,
      order: 4
    },
    {
      id: 'projects',
      label: 'Proyectos',
      route: '/projects',
      icon: '🚀',
      description: 'Portfolio de proyectos realizados',
      enabled: false,
      order: 5
    },
    {
      id: 'gallery',
      label: 'Certificaciones',
      route: '/gallery',
      icon: '🏆',
      description: 'Certificaciones y logros obtenidos',
      enabled: false,
      order: 6
    },
    {
      id: 'clients',
      label: 'Clientes',
      route: '/clients',
      icon: '🤝',
      description: 'Empresas que confían en Corvus TID',
      enabled: true,
      order: 7
    },
    {
      id: 'contact',
      label: 'Contacto',
      route: '/contact',
      icon: '📞',
      description: 'Información de contacto y formulario',
      enabled: true,
      order: 8
    }
  ],
  footerMenus: [
    {
      id: 'privacy',
      label: 'Política de Privacidad',
      route: '/privacy',
      icon: '🔒',
      description: 'Política de privacidad y protección de datos',
      enabled: false,
      order: 1
    },
    {
      id: 'terms',
      label: 'Términos de Servicio',
      route: '/terms',
      icon: '📄',
      description: 'Términos y condiciones de servicio',
      enabled: false,
      order: 2
    },
    {
      id: 'blog',
      label: 'Blog',
      route: '/blog',
      icon: '📝',
      description: 'Artículos y noticias del sector',
      enabled: false,
      order: 3
    }
  ],
  mobileMenus: [
    {
      id: 'quick-contact',
      label: 'Contacto Rápido',
      route: '/quick-contact',
      icon: '📱',
      description: 'Formulario de contacto simplificado',
      enabled: false,
      order: 1
    }
  ]
};

// Función para obtener menús habilitados
export function getEnabledMenus(menuType: 'primary' | 'footer' | 'mobile' = 'primary'): MenuItem[] {
  let menus: MenuItem[] = [];
  
  switch (menuType) {
    case 'primary':
      menus = MENU_CONFIG.primaryMenus;
      break;
    case 'footer':
      menus = MENU_CONFIG.footerMenus || [];
      break;
    case 'mobile':
      menus = MENU_CONFIG.mobileMenus || [];
      break;
  }
  
  return menus
    .filter(menu => menu.enabled)
    .sort((a, b) => a.order - b.order);
}

// Función para obtener un menú específico por ID
export function getMenuById(id: string, menuType: 'primary' | 'footer' | 'mobile' = 'primary'): MenuItem | undefined {
  const menus = getEnabledMenus(menuType);
  return menus.find(menu => menu.id === id);
}

// Función para habilitar/deshabilitar un menú
export function toggleMenuEnabled(id: string, enabled: boolean, menuType: 'primary' | 'footer' | 'mobile' = 'primary'): void {
  let menus: MenuItem[] = [];
  
  switch (menuType) {
    case 'primary':
      menus = MENU_CONFIG.primaryMenus;
      break;
    case 'footer':
      menus = MENU_CONFIG.footerMenus || [];
      break;
    case 'mobile':
      menus = MENU_CONFIG.mobileMenus || [];
      break;
  }
  
  const menu = menus.find(m => m.id === id);
  if (menu) {
    menu.enabled = enabled;
  }
}

// Función para actualizar el orden de los menús
export function updateMenuOrder(id: string, newOrder: number, menuType: 'primary' | 'footer' | 'mobile' = 'primary'): void {
  let menus: MenuItem[] = [];
  
  switch (menuType) {
    case 'primary':
      menus = MENU_CONFIG.primaryMenus;
      break;
    case 'footer':
      menus = MENU_CONFIG.footerMenus || [];
      break;
    case 'mobile':
      menus = MENU_CONFIG.mobileMenus || [];
      break;
  }
  
  const menu = menus.find(m => m.id === id);
  if (menu) {
    menu.order = newOrder;
  }
}

// Configuración de rutas disponibles
export const AVAILABLE_ROUTES = {
  HOME: '/',
  ABOUT: '/aboutMe',
  SERVICES: '/skills',
  EXPERIENCE: '/experience',
  PROJECTS: '/projects',
  GALLERY: '/gallery',
  CONTACT: '/contact',
  PRIVACY: '/privacy',
  TERMS: '/terms',
  BLOG: '/blog',
  QUICK_CONTACT: '/quick-contact'
};

// Mapeo de rutas reales del sistema
export const ROUTE_MAPPING: { [key: string]: string } = {
  '/': '/',
  '/about': '/aboutMe',
  '/services': '/skills',
  '/experience': '/experience',
  '/projects': '/projects',
  '/gallery': '/gallery',
  '/contact': '/contact',
  '/privacy': '/privacy',
  '/terms': '/terms',
  '/blog': '/blog',
  '/quick-contact': '/quick-contact'
};

// Función para obtener la ruta real del sistema
export function getRealRoute(configRoute: string): string {
  return ROUTE_MAPPING[configRoute] || configRoute;
}